import { flow, makeObservable } from 'mobx';
import moment from 'moment';

import { hasValue } from 'src/shared/utils/common';
import { convertHoursToSeconds } from 'src/shared/utils/date';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import type { Directories } from 'src/store/directories/directories.store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import { calculateDurationByCommercialSpeed } from '../../utils';
import type { FormDirectoryValuesService } from '../form-directory-values-service';
import type { FormStore } from '../form.entity';

import type { RigOperationSection } from './rig-operation-section.entity';
import { RigOperationStage } from './rig-operation-stage.entity';
import type { RigOperation } from './rig-operation.entity';
import type { RigOperationsService } from './rig-operations-service';

export class RestoreWorkStagesToDirectoryValuesService {
  private readonly formStore: FormStore;
  private readonly rigOperationsService: RigOperationsService;
  private readonly directories: Directories;
  private readonly notificationsService: NotificationsStore;
  private readonly formDirectoryValuesService: FormDirectoryValuesService;

  constructor(
    form: FormStore,
    rigOperationsService: RigOperationsService,
    directoriers: Directories,
    notificationsService: NotificationsStore,
  ) {
    this.formStore = form;
    this.rigOperationsService = rigOperationsService;
    this.directories = directoriers;
    this.notificationsService = notificationsService;
    this.formDirectoryValuesService = form.formDirectoryValuesService;

    makeObservable(this);
  }

  private async loadWellPassingValueDirectory(startDate: number) {
    const { block, field, fluidType, wellType } = this.formStore.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value) {
      return;
    }

    const year = moment(startDate * 1000).year();

    const [PSDirectory] = await this.directories.loadObjects([
      [
        'Common_WellPassing',
        { blockId: block.value, fieldId: field.value, fluidTypeId: fluidType.value, wellTypeId: wellType.value, year },
      ],
    ]);

    return PSDirectory;
  }

  private async loadCommercialSpeedDirectory(rigId: number, startDate: number) {
    const { block, field, fluidType, wellType } = this.formStore.fields;
    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value) {
      return;
    }

    const modelId = this.directories.getObject('Common_Rig')?.find((rig) => rig.id === rigId)?.data['modelId'];

    if (!modelId) {
      return;
    }

    const year = moment(startDate * 1000).year();

    const [CSDirectory] = await this.directories.loadObjects([
      [
        'Common_CommercialSpeed',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          modelId,
          year,
        },
      ],
    ]);

    return CSDirectory;
  }

  async restoreRigOperation({
    startDate,
    rigOperation,
    calculatePSandCS,
  }: {
    startDate: number;
    rigOperation: RigOperation;
    calculatePSandCS: boolean;
  }): Promise<RigOperation> {
    const stageTypes =
      this.directories
        .getObject('GOplan_Dict_WellLifeCycleStage')
        ?.filter((dv) => dv.status === 'ACTIVE')
        .sort((a, b) => (a.data['order'] as number) - (b.data['order'] as number)) ?? [];
    const stageDurations =
      this.directories.getObject('Common_StageDuration')?.filter((dv) => dv.status === 'ACTIVE') ?? [];
    const modelId = this.directories.getObject('Common_Rig')?.find((rig) => rig.id === rigOperation.rigId)?.data[
      'modelId'
    ];
    const durationByStages = stageDurations.find((dv) => dv.data['modelId'] === modelId)?.data['durationByStage'];

    if (!Array.isArray(durationByStages)) {
      this.notificationsService.showErrorMessageT(
        'Отсутствуют данные продолжительности этапов для заданных параметров. Приведение к справочным данным не может быть закончено',
      );
      throw new Error('Отсутствуют необходимые справочные значения. durationByStage');
    }

    const stages: RigOperationStage[] = [];

    for (const { id: stageTypeId } of stageTypes) {
      const prevStage = stages[stages.length - 1];

      const durationFromDirectory = durationByStages?.find(
        (d) => parsePrimitivesFromString(d['wellLifeCycleStageId']) === stageTypeId,
      )?.['duration'];

      const isDrillingStage = this.rigOperationsService.isDrillingStage(stageTypeId as number);

      const currentStageStartDate = prevStage?.endDate ?? startDate;

      if (!isDrillingStage && !hasValue(durationFromDirectory)) {
        this.notificationsService.showErrorMessageT(
          'Отсутствуют данные продолжительности этапов для заданных параметров. Приведение к справочным данным не может быть закончено',
        );
        throw new Error(`Отсутствуют необходимые справочные значения. Duration: ${durationFromDirectory}`);
      }

      if (!isDrillingStage) {
        const endDate = currentStageStartDate + convertHoursToSeconds(durationFromDirectory * 24);

        const newStage = new RigOperationStage(stages.length, {
          startDate: currentStageStartDate,
          endDate,
          duration: durationFromDirectory,
          wellLifeCycleStageId: stageTypeId as number,
        });

        stages.push(newStage);
        continue;
      } else {
        await Promise.all([
          this.loadCommercialSpeedDirectory(rigOperation.rigId as number, currentStageStartDate),
          this.loadWellPassingValueDirectory(currentStageStartDate),
        ]);

        const commercialSpeed = (() => {
          if (!this.rigOperationsService.isSectionDetailingOrManyRigOperations) {
            const { commercialSpeed } = this.formStore.fields;

            return hasValue(commercialSpeed?.value) && typeof commercialSpeed?.value === 'number'
              ? commercialSpeed.value
              : null;
          } else {
            return this.formDirectoryValuesService.getCommercialSpeedFromDirectories(
              rigOperation.rigId as number,
              currentStageStartDate,
              true,
            );
          }
        })();
        const passingValue = (() => {
          if (!this.rigOperationsService.isSectionDetailingOrManyRigOperations) {
            const { passingValue } = this.formStore.fields;

            return hasValue(passingValue?.value) && typeof passingValue?.value === 'number' ? passingValue.value : null;
          } else {
            return this.formDirectoryValuesService.getDirPassingValue(currentStageStartDate, true);
          }
        })();

        if (!hasValue(commercialSpeed) || !hasValue(passingValue)) {
          if (!this.rigOperationsService.isSectionDetailingOrManyRigOperations) {
            this.notificationsService.showErrorMessageT(
              'Не заполнены поля проходки и/или коммерческой скорости. Приведение к справочным данным не может быть закончено',
            );
            throw new Error(
              `Не заполнены поля проходки и/или коммерческой скорости. PassingValue: ${passingValue} CS: ${commercialSpeed}`,
            );
          }

          this.notificationsService.showErrorMessageT(
            'Отсутствуют необходимые справочные значения проходки и/или коммерческой скорости. Приведение к справочным данным не может быть закончено',
          );
          throw new Error(
            `Отсутствуют необходимые справочные значения. PassingValue: ${passingValue} CS: ${commercialSpeed}`,
          );
        }

        const duration = this.formDirectoryValuesService.applyWinterCoefToDuration(
          calculateDurationByCommercialSpeed(passingValue, commercialSpeed),
          currentStageStartDate,
        );
        const endDate = currentStageStartDate + convertHoursToSeconds(duration * 24);

        const newStage = new RigOperationStage(stages.length, {
          startDate: currentStageStartDate,
          endDate,
          duration,
          wellLifeCycleStageId: stageTypeId as number,
          commercialSpeed,
          passingValue,
        });

        stages.push(newStage);
      }
    }

    this.rigOperationsService.setStages(rigOperation, stages);

    return rigOperation;
  }

  // Пока что неиспользуемый метод.
  @flow.bound
  private async *restoreRigOperations(rigOperations: RigOperation[]) {
    // TODO: сделать тут валидацию с сообщениями для пользователя
    if (rigOperations.some((ro) => !ro.rigId || !ro.stages[0]?.startDate)) {
      return;
    }

    try {
      let prevRigOperation: RigOperation | null = null;

      for (const rigOperation of rigOperations) {
        const _prevRigOperation: RigOperation | null = prevRigOperation;

        const startDate = ((): number => {
          const currentRigStartDate = rigOperation.stages[0].startDate!;
          const prevRigEndDate = (() => {
            if (!_prevRigOperation) {
              return null;
            }

            const lastStage: RigOperationStage | null =
              _prevRigOperation.stages[_prevRigOperation.stages.length - 1] ?? null;
            const lastSection: RigOperationSection | null = lastStage.sections[lastStage.sections.length - 1] ?? null;

            return (lastSection ?? lastStage)?.endDate ?? null;
          })();

          if (!prevRigEndDate || currentRigStartDate >= prevRigEndDate) {
            return currentRigStartDate;
          }

          return prevRigEndDate;
        })();

        const restoredRigOperation = await this.restoreRigOperation({
          startDate,
          rigOperation,
          calculatePSandCS: rigOperations.length > 1,
        });
        yield;

        prevRigOperation = restoredRigOperation;
      }
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  init = () => {};
}
